import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Form,
  required,
  useDataProvider,
  useGetList,
  useGetOne,
  useNotify,
} from "ra-core";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { Send } from "lucide-react";
import { Link } from "react-router";
import { TextInput } from "@/components/admin/text-input";
import { SelectInput } from "@/components/admin/select-input";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DEFAULT_MAIL_TEMPLATES,
  getMissingMailVariables,
  renderMailTemplate,
  type MailTemplate,
} from "../../../../supabase/functions/_shared/mailTemplates";
import { EmailPreview } from "./EmailPreview";
import { MailAttachments } from "./MailAttachments";
import {
  mergeMailAttachments,
  validateMailAttachments,
} from "../../../../supabase/functions/_shared/mailAttachments";
import { getTemplateContext, TEMPLATE_FIELD_LABELS } from "./templateFields";
import type { CrmDataProvider } from "../providers/types";
import type {
  Company,
  EmailDraft,
  EmailTemplate,
  MailAccount,
  SendMailInput,
} from "../types";

export type RecipientOption = {
  email: string;
  label: string;
  name?: string;
  contactId?: number | null;
};
type Props = {
  company: Company;
  account?: MailAccount | null;
  recipients: RecipientOption[];
  initial: SendMailInput;
  draft?: EmailDraft;
  initialTemplate?: MailTemplate;
  onClose: () => void;
  onSaved: () => void;
};
type Values = SendMailInput & {
  templateId?: string;
  proposalUrl?: string;
  templateName?: string;
  variables?: Record<string, string>;
};

export const MailComposer = (props: Props) => {
  const contentRef = useRef<HTMLDivElement>(null);
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) props.onClose();
      }}
    >
      <DialogContent
        ref={contentRef}
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          contentRef.current?.focus({ preventScroll: true });
        }}
        className="max-h-[92dvh] overflow-y-auto sm:max-w-2xl [&>button]:hidden"
        onInteractOutside={(event) => event.preventDefault()}
        onEscapeKeyDown={(event) => event.preventDefault()}
      >
        <Form
          className="min-w-0"
          defaultValues={{
            ...props.initial,
            templateId: props.initialTemplate
              ? String(props.initialTemplate.id)
              : undefined,
          }}
          onSubmit={() => undefined}
        >
          <ComposerForm {...props} />
        </Form>
      </DialogContent>
    </Dialog>
  );
};

const ComposerForm = ({
  company,
  account,
  recipients,
  initial,
  initialTemplate,
  draft: initialDraft,
  onClose,
  onSaved,
}: Props) => {
  const dataProvider = useDataProvider<CrmDataProvider>();
  const notify = useNotify();
  const queryClient = useQueryClient();
  const {
    watch,
    getValues,
    setValue,
    reset,
    formState: { isDirty },
  } = useFormContext<Values>();
  const values = watch();
  const [draft, setDraft] = useState(initialDraft);
  const [preview, setPreview] = useState(!!initialDraft);
  const formRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const dialog = formRef.current?.closest<HTMLElement>('[role="dialog"]');
    dialog?.scrollTo({ top: 0 });
    dialog?.focus({ preventScroll: true });
  }, [preview]);
  const [filesBusy, setFilesBusy] = useState(false);
  const [saveTemplateOpen, setSaveTemplateOpen] = useState(false);
  const [localError, setLocalError] = useState("");
  const lastApplied = useRef({ subject: initial.subject, text: initial.text });
  const { data: customTemplates = [], error: templateError } =
    useGetList<EmailTemplate>("email_templates", {
      pagination: { page: 1, perPage: 100 },
      sort: { field: "name", order: "ASC" },
    });
  const templates = [...DEFAULT_MAIL_TEMPLATES, ...customTemplates];
  if (
    initialTemplate &&
    !templates.some((item) => item.id === initialTemplate.id)
  )
    templates.push(initialTemplate);
  const builtinTemplate = DEFAULT_MAIL_TEMPLATES.find(
    (item) => String(item.id) === values.templateId,
  );
  const {
    data: personalTemplate,
    error: attachmentLoadError,
    refetch: retryTemplateFiles,
  } = useGetOne<MailTemplate>(
    "email_templates",
    { id: values.templateId || 0, meta: { templateFiles: true } },
    { enabled: !!values.templateId && !builtinTemplate },
  );
  const chosenTemplate =
    builtinTemplate || (values.templateId ? personalTemplate : undefined);

  const recipient = recipients.find((item) => item.email === values.to);
  const missing = getMissingMailVariables(`${values.subject}\n${values.text}`);
  const locked = !!draft && draft.status !== "pending";
  const dirty = isDirty;
  useEffect(() => {
    if (!dirty) return;
    const preventLoss = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener("beforeunload", preventLoss);
    return () => window.removeEventListener("beforeunload", preventLoss);
  }, [dirty]);
  const save = useMutation({
    mutationFn: async () => {
      const current = getValues();
      const attachments = validateMailAttachments(current.attachments || []);
      const selected = recipients.find((item) => item.email === current.to);
      const message: SendMailInput = {
        companyId: Number(company.id),
        contactId: selected?.contactId ?? null,
        threadId: current.threadId,
        to: current.to,
        subject: current.subject,
        text: current.text,
        purpose: current.purpose,
        attachments,
      };
      const saved = await dataProvider.mailDraftSave(
        message,
        draft?.id,
        draft?.version,
      );
      setDraft(saved);
      reset({ ...current, ...saved.message });
      onSaved();
      return saved;
    },
    onError: (error: Error) => setLocalError(error.message),
  });
  const approve = useMutation({
    mutationFn: async () => {
      if (!draft || dirty)
        throw new Error("Сначала сохраните изменения и проверьте письмо");
      return dataProvider.mailDraftApprove(
        draft.id,
        draft.version,
        account!.email,
      );
    },
    onSuccess: () => {
      onSaved();
      notify("Письмо отправлено");
      onClose();
    },
    onError: async (error: Error) => {
      setLocalError(error.message);
      if (draft) {
        try {
          const result = await dataProvider.getOne<EmailDraft>("email_drafts", {
            id: draft.id,
          });
          setDraft(result.data);
        } catch {
          /* keep the reviewed content */
        }
      }
      onSaved();
    },
  });
  const saveTemplate = useMutation({
    mutationFn: () =>
      dataProvider.create<EmailTemplate>("email_templates", {
        data: {
          name: values.templateName?.trim(),
          subject: values.subject,
          text: values.text,
          attachments: validateMailAttachments(values.attachments || []),
        },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["email_templates"] });
      setSaveTemplateOpen(false);
      notify("Шаблон сохранён");
    },
    onError: (error: Error) => setLocalError(error.message),
  });
  const busy =
    save.isPending || approve.isPending || saveTemplate.isPending || filesBusy;
  const canSave =
    !!recipient && !!values.subject?.trim() && !!values.text?.trim();
  const templatePreview = chosenTemplate
    ? renderMailTemplate(chosenTemplate, {
        ...Object.fromEntries(
          Object.entries(values.variables || {})
            .filter(([, value]) => value?.trim())
            .map(([key, value]) => [key, value.trim()]),
        ),
        ...getTemplateContext({
          companyName: company.name,
          recipientName: recipient?.name,
          senderName: account?.display_name || account?.email,
        }),
        ...(values.proposalUrl?.trim()
          ? { proposal_url: `Предложение: ${values.proposalUrl.trim()}` }
          : chosenTemplate.attachments?.length || values.attachments?.length
            ? { proposal_url: "Коммерческое предложение — во вложении." }
            : {}),
      })
    : undefined;
  const contextFields = [
    "company_name",
    "contact_name",
    ...(account ? ["sender_name"] : []),
  ];
  const templateFields = chosenTemplate
    ? getMissingMailVariables(
        `${chosenTemplate.subject}\n${chosenTemplate.text}`,
      ).filter(
        (name) =>
          !contextFields.includes(name) &&
          name !== "proposal_url" &&
          Object.hasOwn(TEMPLATE_FIELD_LABELS, name),
      )
    : [];
  const applyTemplate = () => {
    if (!templatePreview) return;
    if (
      (values.text?.trim() || values.subject?.trim()) &&
      (values.text !== lastApplied.current.text ||
        values.subject !== lastApplied.current.subject) &&
      !window.confirm(
        "Заменить изменённую вручную тему и текст письма содержимым шаблона?",
      )
    )
      return;
    let attachments;
    try {
      attachments = mergeMailAttachments(
        values.attachments || [],
        chosenTemplate?.attachments || [],
      );
    } catch (error) {
      setLocalError(
        error instanceof Error
          ? error.message
          : "Не удалось добавить файлы шаблона",
      );
      return;
    }
    setLocalError("");
    setValue("attachments", attachments, { shouldDirty: true });
    setValue("subject", templatePreview.subject, { shouldDirty: true });
    setValue("text", templatePreview.text, { shouldDirty: true });
    lastApplied.current = templatePreview;
  };
  const showPreview = async () => {
    setLocalError("");
    try {
      if (dirty || !draft) await save.mutateAsync();
      setPreview(true);
    } catch {
      /* rendered below */
    }
  };
  const close = async () => {
    if (busy) return;
    if (dirty && canSave && !locked) {
      try {
        await save.mutateAsync();
        onClose();
      } catch {
        /* retain the form */
      }
    } else if (
      !dirty ||
      window.confirm("Закрыть письмо без сохранения незаполненного черновика?")
    )
      onClose();
  };

  return (
    <div ref={formRef} className="min-w-0 space-y-5">
      <DialogHeader>
        <DialogTitle>
          {preview
            ? "Письмо на утверждение"
            : draft
              ? "Редактировать черновик"
              : "Новое письмо"}
        </DialogTitle>
        <DialogDescription>
          {preview
            ? "Проверьте адрес, текст и вложения. Кнопка утверждения отправит это письмо."
            : "Выберите шаблон или напишите свой текст. Черновик сохранится перед проверкой."}
        </DialogDescription>
      </DialogHeader>
      {preview ? (
        <div className="space-y-4 text-sm">
          <EmailPreview
            from={
              account?.display_name
                ? `${account.display_name} <${account.email}>`
                : account?.email || "Подключите почту в настройках"
            }
            to={recipient?.label || values.to}
            subject={values.subject}
            text={values.text}
          />
          <p className="text-muted-foreground">
            Назначение:{" "}
            {values.purpose === "marketing"
              ? "Рекламное предложение"
              : "Деловая переписка"}
          </p>
          <MailAttachments value={values.attachments} />
          {!!missing.length && (
            <p role="alert" className="text-destructive">
              Заполните переменные: {missing.join(", ")}. Нажмите
              «Редактировать».
            </p>
          )}
          {locked && (
            <p role="status" className="text-muted-foreground">
              {draft?.status === "sent"
                ? "Письмо отправлено."
                : draft?.status === "sending"
                  ? "Отправка уже началась. Обновите список, чтобы проверить результат."
                  : "Отправка завершилась ошибкой. Проверьте исходящие перед созданием нового письма: сервер мог принять его."}{" "}
              {draft?.error}
            </p>
          )}
        </div>
      ) : (
        <fieldset disabled={busy} className="min-w-0 space-y-4">
          <SelectInput
            source="to"
            label="Кому"
            choices={recipients.map((item) => ({
              id: item.email,
              name: item.label,
            }))}
            validate={required()}
            translateChoice={false}
          />
          <div className="space-y-3 rounded-md bg-muted/40 p-3">
            <Link
              className="text-sm underline underline-offset-4"
              to="/email_templates"
              target="_blank"
              rel="noreferrer"
            >
              Открыть библиотеку шаблонов
            </Link>
            <SelectInput
              source="templateId"
              label="Шаблон письма"
              choices={templates.map((item) => ({
                id: String(item.id),
                name: item.name,
              }))}
              translateChoice={false}
            />
            {chosenTemplate &&
              getMissingMailVariables(
                `${chosenTemplate.subject}\n${chosenTemplate.text}`,
              ).includes("proposal_url") && (
                <TextInput
                  source="proposalUrl"
                  label="Ссылка на КП"
                  type="url"
                  helperText="Можно прикрепить файл ниже и затем применить шаблон."
                />
              )}
            {templateFields.length > 0 && (
              <div className="space-y-3 border-t pt-3">
                <p className="text-sm font-medium">Данные для этого письма</p>
                {templateFields.map((name) => (
                  <TextInput
                    key={name}
                    source={`variables.${name}`}
                    label={TEMPLATE_FIELD_LABELS[name]}
                    multiline
                    rows={name === "offer_scope" ? 4 : 2}
                    maxLength={10_000}
                  />
                ))}
                <p className="text-xs text-muted-foreground">
                  Укажите реальные условия и нажмите «Подставить в письмо».
                  Примеры из библиотеки сюда не переносятся.
                </p>
              </div>
            )}
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={
                !chosenTemplate || (!!attachmentLoadError && !builtinTemplate)
              }
              onClick={applyTemplate}
            >
              Подставить в письмо
            </Button>
            {templatePreview && (
              <details className="space-y-3">
                <summary className="cursor-pointer text-sm">
                  Предпросмотр шаблона с этими данными
                </summary>
                <EmailPreview
                  {...templatePreview}
                  from={account?.email || "Почта не подключена"}
                  to={recipient?.label || values.to || "Выберите получателя"}
                />
              </details>
            )}
            <p className="text-xs text-muted-foreground">
              Заменит тему и текст, добавит файлы шаблона. Уже прикреплённые
              файлы сохранятся. После смены получателя проверьте обращение.
            </p>
            {!!values.templateId &&
              !builtinTemplate &&
              !chosenTemplate &&
              !attachmentLoadError && (
                <p role="status" className="text-sm">
                  Загрузка шаблона и файлов…
                </p>
              )}
            {!!values.templateId && !builtinTemplate && attachmentLoadError && (
              <div role="alert" className="space-y-2 text-sm text-destructive">
                <p>Не удалось загрузить шаблон и его файлы.</p>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => retryTemplateFiles()}
                >
                  Повторить загрузку шаблона
                </Button>
              </div>
            )}
            {!!chosenTemplate?.attachments?.length && (
              <p className="text-xs text-muted-foreground">
                Файлов в шаблоне: {chosenTemplate.attachments.length}. Они
                добавятся при подстановке.
              </p>
            )}
            {templateError && (
              <p role="alert" className="text-sm text-destructive">
                Не удалось загрузить ваши шаблоны. Стандартные доступны.
              </p>
            )}
          </div>
          <SelectInput
            source="purpose"
            label="Назначение"
            choices={[
              { id: "business", name: "Деловая переписка" },
              { id: "marketing", name: "Рекламное предложение" },
            ]}
            translateChoice={false}
          />
          {values.purpose === "marketing" && (
            <p className="text-xs text-muted-foreground">
              Отправка доступна при зафиксированном согласии компании.
            </p>
          )}
          <TextInput
            source="subject"
            label="Тема"
            maxLength={500}
            validate={required()}
          />
          <TextInput
            source="text"
            label="Сообщение"
            multiline
            rows={9}
            maxLength={100_000}
            validate={required()}
          />
          <MailAttachments
            value={values.attachments}
            disabled={
              save.isPending || approve.isPending || saveTemplate.isPending
            }
            onBusyChange={setFilesBusy}
            onChange={(attachments) =>
              setValue("attachments", attachments, { shouldDirty: true })
            }
          />
          {saveTemplateOpen ? (
            <div className="space-y-2 border-t pt-3">
              <TextInput
                source="templateName"
                label="Название шаблона"
                maxLength={100}
              />
              <p className="text-xs text-muted-foreground">
                Для повторного использования замените имена на{" "}
                {"{{company_name}}"}, {"{{contact_name}}"} и {"{{sender_name}}"}
                . Файлы тоже сохранятся в шаблон: уберите персональные материалы
                перед сохранением.
              </p>
              <Button
                type="button"
                variant="outline"
                disabled={
                  !values.templateName?.trim() ||
                  !values.subject?.trim() ||
                  !values.text?.trim()
                }
                onClick={() => saveTemplate.mutate()}
              >
                Сохранить шаблон
              </Button>
            </div>
          ) : (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setSaveTemplateOpen(true)}
            >
              Сохранить письмо и файлы как шаблон
            </Button>
          )}
        </fieldset>
      )}
      {localError && (
        <p role="alert" className="text-sm text-destructive">
          {localError}
        </p>
      )}
      <DialogFooter className="flex-wrap gap-2">
        <Button type="button" variant="ghost" disabled={busy} onClick={close}>
          {dirty && canSave ? "Сохранить и закрыть" : "Закрыть"}
        </Button>
        {preview ? (
          <>
            {!locked && (
              <Button
                type="button"
                variant="outline"
                disabled={busy}
                onClick={() => {
                  setPreview(false);
                  setLocalError("");
                }}
              >
                Редактировать
              </Button>
            )}
            <Button
              type="button"
              disabled={
                busy ||
                locked ||
                !!missing.length ||
                !account ||
                !recipient ||
                dirty
              }
              className="h-auto min-h-11 whitespace-normal px-3 py-2"
              onClick={() => approve.mutate()}
            >
              <Send className="size-4" />
              {approve.isPending ? "Отправляем…" : "Утвердить и отправить"}
            </Button>
          </>
        ) : (
          <Button
            type="button"
            disabled={busy || !canSave}
            onClick={showPreview}
          >
            {save.isPending ? "Сохраняем…" : "Проверить письмо"}
          </Button>
        )}
      </DialogFooter>
    </div>
  );
};
